import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Search, Users, BookOpen, UserPlus, UserMinus, 
  Sparkles, ExternalLink, ShoppingBag, Filter, Plus, Loader2, Trash2, Link as LinkIcon, PenLine, Upload, Image as ImageIcon
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MembersPage = () => {
  const { user, api } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'directory';
  const [members, setMembers] = useState([]);
  const [newcomers, setNewcomers] = useState([]);
  const [publishedWorks, setPublishedWorks] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [followingStatus, setFollowingStatus] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [membersRes, newcomersRes, worksRes, genresRes] = await Promise.all([
          api.get('/members', { params: { search: searchQuery || undefined, genre: (selectedGenre && selectedGenre !== 'all') ? selectedGenre : undefined, sort: sortBy } }),
          api.get('/members/newcomers'),
          api.get('/members/published-works'),
          api.get('/genres')
        ]);
        setMembers(membersRes.data);
        setNewcomers(newcomersRes.data);
        setPublishedWorks(worksRes.data);
        setGenres(genresRes.data);

        if (user) {
          try {
            const followingRes = await api.get(`/users/${user.id}/following`);
            const followingIds = {};
            followingRes.data.forEach(f => { followingIds[f.id] = true; });
            setFollowingStatus(followingIds);
          } catch (e) { console.error('Failed to fetch following:', e); }
        }
      } catch (error) {
        console.error('Failed to fetch members:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api, searchQuery, selectedGenre, sortBy, user]);

  const handleFollow = async (memberId) => {
    if (!user) return;
    try {
      if (followingStatus[memberId]) {
        await api.delete(`/users/${memberId}/follow`);
        setFollowingStatus(prev => ({ ...prev, [memberId]: false }));
      } else {
        await api.post(`/users/${memberId}/follow`);
        setFollowingStatus(prev => ({ ...prev, [memberId]: true }));
      }
    } catch (error) {
      console.error('Failed to toggle follow:', error);
    }
  };

  const MemberCard = ({ member }) => {
    const avatarSrc = member.avatar_url?.startsWith('/api')
      ? `${API_URL}${member.avatar_url}`
      : member.avatar_url;

    return (
      <Card className="bg-white border-brand-primary/10 card-hover" data-testid={`member-card-${member.id}`}>
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <Link to={`/writer/${member.id}`}>
              <Avatar className="h-14 w-14 ring-2 ring-brand-primary/10">
                <AvatarImage src={avatarSrc} />
                <AvatarFallback className="bg-brand-primary text-white text-lg">
                  {(member.display_name || member.username)?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </Link>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <Link to={`/writer/${member.id}`} className="hover:text-brand-accent transition-colors">
                  <h3 className="font-semibold text-text-primary truncate">
                    {member.display_name || member.username}
                  </h3>
                </Link>
                {user && user.id !== member.id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleFollow(member.id)}
                    className={followingStatus[member.id] ? 'text-text-muted' : 'text-brand-accent border-brand-accent/30'}
                    data-testid={`follow-btn-${member.id}`}
                  >
                    {followingStatus[member.id] ? (
                      <><UserMinus className="w-3 h-3 mr-1" /> Unfollow</>
                    ) : (
                      <><UserPlus className="w-3 h-3 mr-1" /> Follow</>
                    )}
                  </Button>
                )}
              </div>
              <p className="text-sm text-text-muted">@{member.username}</p>
              {member.bio && (
                <p className="text-sm text-text-secondary mt-1 line-clamp-2">{member.bio}</p>
              )}
              <div className="flex items-center gap-4 mt-2">
                <span className="text-xs text-text-muted flex items-center gap-1">
                  <BookOpen className="w-3 h-3" /> {member.story_count} works
                </span>
                <span className="text-xs text-text-muted flex items-center gap-1">
                  <Users className="w-3 h-3" /> {member.follower_count} followers
                </span>
              </div>
              {member.favorite_genres?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {member.favorite_genres.slice(0, 3).map(g => (
                    <Badge key={g} variant="outline" className="text-xs px-2 py-0">{g}</Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const handleDeleteBook = async (workId) => {
    if (!window.confirm('Remove this book from the Bookshelf?')) return;
    try {
      await api.delete(`/published-works/${workId}`);
      setPublishedWorks(prev => prev.filter(w => w.id !== workId));
      toast.success('Book removed from the Bookshelf.');
    } catch {
      toast.error('Could not remove book.');
    }
  };

  const handleEditBook = async (workId, updates) => {
    try {
      const res = await api.put(`/published-works/${workId}`, updates);
      setPublishedWorks(prev => prev.map(w => w.id === workId ? res.data : w));
      toast.success('Book updated!');
      return true;
    } catch {
      toast.error('Could not update book.');
      return false;
    }
  };

  const PublishedWorkCard = ({ work }) => {
    const isOwner = user && (work.author_id === user.id || work.user_id === user.id);
    const [editing, setEditing] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [editForm, setEditForm] = useState({
      title: work.title, description: work.description || '',
      cover_image: work.cover_image || '', purchase_url: work.purchase_url || ''
    });

    const handleCoverUpload = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/upload/story-image', formData);
        setEditForm(prev => ({ ...prev, cover_image: res.data.image_url }));
        toast.success('Cover uploaded!');
      } catch { toast.error('Upload failed'); }
      finally { setUploading(false); }
    };

    const coverSrc = work.cover_image?.startsWith('/api')
      ? `${API_URL}${work.cover_image}` : work.cover_image;

    if (editing) {
      const previewSrc = editForm.cover_image?.startsWith('/api')
        ? `${API_URL}${editForm.cover_image}` : editForm.cover_image;
      return (
        <Card className="bg-white border-brand-accent/20 overflow-hidden" data-testid={`edit-book-${work.id}`}>
          <CardContent className="p-4 space-y-3">
            <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} placeholder="Book Title" />
            <Input value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} placeholder="Short Description" />
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Input value={editForm.cover_image} onChange={(e) => setEditForm({ ...editForm, cover_image: e.target.value })} placeholder="Cover Image URL" className="flex-1" />
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" />
                  <span className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-brand-primary/20 text-xs font-medium text-text-secondary hover:bg-surface-alt transition-colors whitespace-nowrap">
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    Upload
                  </span>
                </label>
              </div>
              {previewSrc && (
                <img src={previewSrc} alt="" className="w-16 h-20 object-cover rounded border" onError={(e) => { e.target.style.display = 'none'; }} />
              )}
            </div>
            <Input value={editForm.purchase_url} onChange={(e) => setEditForm({ ...editForm, purchase_url: e.target.value })} placeholder="Purchase Link" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="sm" className="btn-primary" onClick={async () => {
                const ok = await handleEditBook(work.id, editForm);
                if (ok) setEditing(false);
              }}>Save</Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="bg-white border-brand-primary/10 card-hover overflow-hidden" data-testid={`published-work-${work.id}`}>
        <CardContent className="p-0">
          <div className="flex">
            {coverSrc && (
              <div className="w-24 h-32 flex-shrink-0 bg-surface-alt">
                <img
                  src={coverSrc}
                  alt={work.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            <div className="p-4 flex-1 min-w-0">
              <h4 className="font-semibold text-text-primary truncate">{work.title}</h4>
              <p className="text-sm text-text-muted mt-0.5">by {work.author_name}</p>
              {work.description && (
                <p className="text-xs text-text-secondary mt-1 line-clamp-2">{work.description}</p>
              )}
              <div className="flex items-center gap-3 mt-2">
                {work.purchase_url && (
                  <a
                    href={work.purchase_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand-accent hover:underline"
                    data-testid={`buy-link-${work.id}`}
                  >
                    <ShoppingBag className="w-3 h-3" /> Purchase
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {isOwner && (
                  <div className="flex items-center gap-1 ml-auto">
                    <button
                      onClick={() => setEditing(true)}
                      className="p-1 rounded text-text-muted hover:text-brand-accent transition-colors"
                      title="Edit listing"
                      data-testid={`edit-book-btn-${work.id}`}
                    >
                      <PenLine className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteBook(work.id)}
                      className="p-1 rounded text-text-muted hover:text-red-500 transition-colors"
                      title="Remove listing"
                      data-testid={`delete-book-btn-${work.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="members-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-text-primary mb-2">Community Members</h1>
        <p className="text-text-secondary">Discover writers, explore published works, and connect</p>
      </div>

      <Tabs defaultValue={initialTab}>
        <TabsList className="bg-surface-alt mb-6">
          <TabsTrigger value="directory" className="gap-2" data-testid="tab-directory">
            <Users className="w-4 h-4" /> Directory
          </TabsTrigger>
          <TabsTrigger value="newcomers" className="gap-2" data-testid="tab-newcomers">
            <Sparkles className="w-4 h-4" /> Newcomers
          </TabsTrigger>
          <TabsTrigger value="bookshelf" className="gap-2" data-testid="tab-bookshelf">
            <ShoppingBag className="w-4 h-4" /> Bookshelf
          </TabsTrigger>
        </TabsList>

        {/* Directory Tab */}
        <TabsContent value="directory">
          {/* Search & Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <Input
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="member-search-input"
              />
            </div>
            <Select value={selectedGenre} onValueChange={setSelectedGenre}>
              <SelectTrigger className="w-full sm:w-44" data-testid="genre-filter">
                <Filter className="w-4 h-4 mr-2 text-text-muted" />
                <SelectValue placeholder="All Genres" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Genres</SelectItem>
                {genres.map(g => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-44" data-testid="sort-filter">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
                <SelectItem value="most_active">Most Active</SelectItem>
                <SelectItem value="most_followed">Most Followed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-20">
              <Users className="w-16 h-16 text-text-muted mx-auto mb-4" />
              <h2 className="font-serif text-2xl font-semibold text-text-primary mb-2">No members found</h2>
              <p className="text-text-secondary">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {members.map(member => (
                <MemberCard key={member.id} member={member} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Newcomers Tab */}
        <TabsContent value="newcomers">
          <div className="mb-6">
            <h2 className="font-serif text-xl font-semibold text-text-primary mb-1">New to the Community</h2>
            <p className="text-sm text-text-secondary">Welcome our newest members and check out their work</p>
          </div>
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}
            </div>
          ) : newcomers.length === 0 ? (
            <div className="text-center py-16">
              <Sparkles className="w-12 h-12 text-text-muted mx-auto mb-3" />
              <p className="text-text-secondary">No newcomers yet</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {newcomers.map(member => {
                const avatarSrc = member.avatar_url?.startsWith('/api')
                  ? `${API_URL}${member.avatar_url}` : member.avatar_url;
                return (
                  <Card key={member.id} className="bg-white border-brand-primary/10 card-hover" data-testid={`newcomer-${member.id}`}>
                    <CardContent className="p-5 text-center">
                      <Link to={`/writer/${member.id}`}>
                        <Avatar className="h-16 w-16 mx-auto mb-3 ring-2 ring-brand-accent/20">
                          <AvatarImage src={avatarSrc} />
                          <AvatarFallback className="bg-brand-accent text-white text-xl">
                            {(member.display_name || member.username)?.[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </Link>
                      <Link to={`/writer/${member.id}`} className="hover:text-brand-accent">
                        <h3 className="font-semibold text-text-primary">{member.display_name || member.username}</h3>
                      </Link>
                      <p className="text-xs text-text-muted">@{member.username}</p>
                      {member.bio && <p className="text-xs text-text-secondary mt-2 line-clamp-2">{member.bio}</p>}
                      <div className="flex items-center justify-center gap-3 mt-3">
                        <span className="text-xs text-text-muted">{member.story_count} works</span>
                        <span className="text-xs text-text-muted">Joined {new Date(member.created_at).toLocaleDateString()}</span>
                      </div>
                      {user && user.id !== member.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3 text-brand-accent border-brand-accent/30"
                          onClick={() => handleFollow(member.id)}
                          data-testid={`follow-newcomer-${member.id}`}
                        >
                          {followingStatus[member.id] ? 'Unfollow' : 'Follow'}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Bookshelf Tab */}
        <TabsContent value="bookshelf">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-serif text-xl font-semibold text-text-primary mb-1">Members' Published Works</h2>
              <p className="text-sm text-text-secondary">Books and publications by our community members</p>
            </div>
            {user && <AddBookDialog api={api} onAdded={(work) => setPublishedWorks(prev => [work, ...prev])} />}
          </div>
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
            </div>
          ) : publishedWorks.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingBag className="w-12 h-12 text-text-muted mx-auto mb-3" />
              <h2 className="font-serif text-xl font-semibold text-text-primary mb-2">No published works yet</h2>
              <p className="text-text-secondary text-sm mb-4">Be the first to share your published book with the community.</p>
              {user && <AddBookDialog api={api} onAdded={(work) => setPublishedWorks(prev => [work, ...prev])} />}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {publishedWorks.map(work => (
                <PublishedWorkCard key={work.id} work={work} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const AddBookDialog = ({ api, onAdded }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', cover_image: '', purchase_url: '' });

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/story-image', formData);
      setForm(prev => ({ ...prev, cover_image: res.data.image_url }));
      toast.success('Cover uploaded!');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const res = await api.post('/published-works', form);
      onAdded(res.data);
      setForm({ title: '', description: '', cover_image: '', purchase_url: '' });
      setOpen(false);
      toast.success('Your book has been added to the Bookshelf!');
    } catch {
      toast.error('Could not add your book. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const previewSrc = form.cover_image?.startsWith('/api')
    ? `${API_URL}${form.cover_image}` : form.cover_image;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-accent gap-2" data-testid="add-book-bookshelf-btn">
          <Plus className="w-4 h-4" />
          Add Your Book
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" aria-describedby="add-book-desc">
        <DialogHeader>
          <DialogTitle>Add a Published Book</DialogTitle>
          <p id="add-book-desc" className="text-sm text-text-muted mt-1">
            Share your published work with the community.
          </p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label>Book Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g., The Quiet Between Us"
              required
              data-testid="bookshelf-title-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Short Description</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="A brief line about your book"
              data-testid="bookshelf-desc-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Cover Image</Label>
            <div className="flex items-center gap-2">
              <Input
                value={form.cover_image}
                onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                placeholder="Paste a URL or upload"
                className="flex-1"
                data-testid="bookshelf-cover-input"
              />
              <label className="cursor-pointer">
                <input type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" data-testid="bookshelf-cover-upload" />
                <span className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-brand-primary/20 text-xs font-medium text-text-secondary hover:bg-surface-alt transition-colors whitespace-nowrap">
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  Upload
                </span>
              </label>
            </div>
            {previewSrc && (
              <img src={previewSrc} alt="" className="w-16 h-20 object-cover rounded border mt-1" onError={(e) => { e.target.style.display = 'none'; }} />
            )}
          </div>
          <div className="space-y-2">
            <Label>Purchase / Read Link</Label>
            <Input
              value={form.purchase_url}
              onChange={(e) => setForm({ ...form, purchase_url: e.target.value })}
              placeholder="https://amazon.com/... or any link"
              data-testid="bookshelf-url-input"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              type="submit"
              className="btn-primary gap-2"
              disabled={saving || !form.title.trim()}
              data-testid="bookshelf-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add Book
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MembersPage;
